import { useCallback, useEffect, useRef, useState } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import Spinner from 'react-bootstrap/Spinner';
import { useAuth0 } from '@auth0/auth0-react';

import Layout from './components/Layout';
import WordsLayout from './components/WordsLayout';
import PublicLayout from './components/PublicLayout';
import WordsPage from './pages/WordsPage';
import ImportWordsPage from './pages/ImportWordsPage';
import LoginPage from './pages/LoginPage';
import UnauthorizedPage from './pages/UnauthorizedPage';
import { parsePermissionsFromAccessToken, userHasRequiredPermission } from './utils/auth';
import './App.css';

const auth0Audience = process.env.REACT_APP_AUTH0_AUDIENCE || '';

const App = () => {
  const { isAuthenticated, isLoading, user, loginWithRedirect, logout, getAccessTokenSilently } =
    useAuth0();
  const location = useLocation();
  const loginTriggeredRef = useRef(false);
  const [permissionState, setPermissionState] = useState({
    loading: true,
    hasPermission: false,
    error: null,
  });

  const getAccessToken = useCallback(
    () => getAccessTokenSilently({ authorizationParams: { audience: auth0Audience } }),
    [getAccessTokenSilently],
  );

  useEffect(() => {
    if (location.pathname === '/login') return;
    if (!isLoading && !isAuthenticated && !loginTriggeredRef.current) {
      loginTriggeredRef.current = true;
      loginWithRedirect({
        appState: { returnTo: location.pathname + location.search },
      }).catch(() => {
        loginTriggeredRef.current = false;
      });
    }
  }, [isAuthenticated, isLoading, location.pathname, location.search, loginWithRedirect]);

  useEffect(() => {
    let isActive = true;

    if (!isAuthenticated) {
      setPermissionState({ loading: true, hasPermission: false, error: null });
      return () => {
        isActive = false;
      };
    }

    if (!auth0Audience) {
      setPermissionState({
        loading: false,
        hasPermission: false,
        error: new Error('Missing Auth0 audience configuration'),
      });
      return () => {
        isActive = false;
      };
    }

    setPermissionState((prev) => ({ ...prev, loading: true, error: null }));

    getAccessToken()
      .then((token) => {
        if (!isActive) return;
        const permissions = parsePermissionsFromAccessToken(token);
        const hasPermission = userHasRequiredPermission(permissions);
        setPermissionState({
          loading: false,
          hasPermission,
          error: hasPermission ? null : new Error('Missing required permission'),
        });
      })
      .catch((err) => {
        if (!isActive) return;
        if (err.error === 'missing_refresh_token' || err.error === 'invalid_grant') {
          loginWithRedirect({
            appState: { returnTo: window.location.pathname + window.location.search },
          });
          return;
        }
        setPermissionState({ loading: false, hasPermission: false, error: err });
      });

    return () => {
      isActive = false;
    };
  }, [getAccessToken, isAuthenticated, loginWithRedirect]);

  if (isLoading && location.pathname !== '/login') {
    return (
      <div
        className='d-flex justify-content-center align-items-center'
        style={{ minHeight: '70vh' }}
      >
        <Spinner animation='border' />
      </div>
    );
  }

  if (isAuthenticated && location.pathname === '/login') {
    return <Navigate to='/' replace />;
  }

  if (!isAuthenticated && !isLoading && location.pathname !== '/login') {
    return <Navigate to='/login' replace />;
  }

  const permissionLoading = isAuthenticated && permissionState.loading;
  const hasPermission = permissionState.hasPermission;

  if (permissionLoading) {
    return (
      <div
        className='d-flex justify-content-center align-items-center'
        style={{ minHeight: '70vh' }}
      >
        <Spinner animation='border' />
      </div>
    );
  }

  if (
    isAuthenticated &&
    !hasPermission &&
    !permissionLoading &&
    location.pathname !== '/unauthorized'
  ) {
    return <Navigate to='/unauthorized' replace />;
  }

  if (isAuthenticated && hasPermission && location.pathname === '/unauthorized') {
    return <Navigate to='/' replace />;
  }

  const userId = user?.sub || null;

  const handleSignOut = () =>
    logout({
      logoutParams: { returnTo: `${window.location.origin}/login` },
    });

  return (
    <Routes>
      <Route element={<PublicLayout />}>
        <Route path='/login' element={<LoginPage />} />
        <Route path='/unauthorized' element={<UnauthorizedPage />} />
      </Route>
      {isAuthenticated && hasPermission && (
        <>
          <Route path='/' element={<WordsLayout user={user} onSignOut={handleSignOut} />}>
            <Route index element={<WordsPage userId={userId} getAccessToken={getAccessToken} />} />
            <Route
              path='words'
              element={<WordsPage userId={userId} getAccessToken={getAccessToken} />}
            />
            <Route
              path='words/:slug'
              element={<WordsPage userId={userId} getAccessToken={getAccessToken} />}
            />
            <Route
              path='words/:lang/:slug'
              element={<WordsPage userId={userId} getAccessToken={getAccessToken} />}
            />
          </Route>
          <Route path='/import' element={<Layout user={user} onSignOut={handleSignOut} />}>
            <Route
              index
              element={<ImportWordsPage userId={userId} getAccessToken={getAccessToken} />}
            />
          </Route>
        </>
      )}
      <Route path='*' element={<Navigate to='/' replace />} />
    </Routes>
  );
};

export default App;
